import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Sparkles, 
  Linkedin, 
  Twitter, 
  Copy, 
  Check, 
  Loader2, 
  TrendingUp,
  Lightbulb,
  Send,
  RefreshCw,
  Settings,
  Image as ImageIcon,
  Zap,
  CheckCircle2,
  Link2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Storage key for persisting social content
const STORAGE_KEY = 'socialPageContent';

// Load saved state from localStorage
const loadSavedState = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Failed to load saved state:', e);
  }
  return null;
};

// Save state to localStorage
const saveState = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save state:', e);
  }
};

// Clear saved state
const clearSavedState = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('Failed to clear saved state:', e);
  }
};

export default function SocialPage() {
  // Load saved state on init
  const savedState = loadSavedState();
  
  const [newsletterContent, setNewsletterContent] = useState(savedState?.newsletterContent || '');
  const [keywords, setKeywords] = useState(savedState?.keywords || '');
  const [url, setUrl] = useState(savedState?.url || '');
  const [wordCount, setWordCount] = useState(savedState?.wordCount || '');
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(savedState?.generatedContent || null);
  const [isApproved, setIsApproved] = useState(savedState?.isApproved || false);
  const [approvingPost, setApprovingPost] = useState(false);
  const [copiedLinkedIn, setCopiedLinkedIn] = useState(false);
  const [copiedX, setCopiedX] = useState(false);
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [posting, setPosting] = useState({ linkedin: false, x: false });
  const [tone, setTone] = useState(savedState?.tone || 'professional');
  const [size, setSize] = useState(savedState?.size || 'shorter');
  const [regeneratingImage, setRegeneratingImage] = useState(false);
  const [showImagePrompt, setShowImagePrompt] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [postTextOnly, setPostTextOnly] = useState(false);
  const [llmProvider, setLlmProvider] = useState(
    savedState?.llmProvider === 'anthropic' ? 'qwen' : (savedState?.llmProvider || 'qwen')
  );

  // Save state whenever important values change
  useEffect(() => {
    const stateToSave = {
      newsletterContent,
      keywords,
      url,
      wordCount,
      generatedContent,
      isApproved,
      tone,
      size,
      llmProvider
    };
    saveState(stateToSave);
  }, [newsletterContent, keywords, url, wordCount, generatedContent, isApproved, tone, size, llmProvider]);

  useEffect(() => {
    fetchSocialAccounts();
  }, []);

  const fetchSocialAccounts = async () => {
    try {
      const res = await axios.get(`${API}/social/accounts`);
      setSocialAccounts(res.data || []);
    } catch (error) {
      console.error('Failed to fetch social accounts');
    }
  };

  // Reset all content and start fresh
  const handleReset = () => {
    setNewsletterContent('');
    setKeywords('');
    setUrl('');
    setWordCount('');
    setGeneratedContent(null);
    setIsApproved(false);
    setTone('professional');
    setSize('shorter');
    setImagePrompt('');
    setPostTextOnly(false);
    clearSavedState();
    toast.success('Content cleared. Ready for new generation.');
  };

  const handleGenerate = async () => {
    // Reset approval state
    setIsApproved(false);
    setLoading(true);
    setGeneratedContent(null);

    try {
      const payload = {
        content: newsletterContent.trim(),
        keywords: keywords.trim(),
        url: url.trim(),
        tone: tone,
        size: size,
        llm_provider: llmProvider
      };
      
      // Add word_count if specified (overrides size)
      if (wordCount && !isNaN(parseInt(wordCount))) {
        payload.word_count = parseInt(wordCount);
      }
      
      const res = await axios.post(`${API}/social/generate`, payload);
      
      // Store text-only content (no image yet)
      setGeneratedContent({
        ...res.data,
        image_url: null // No image until approved
      });
      
      // Show appropriate success message
      if (url.trim()) {
        toast.success('Content generated from URL!');
      } else if (!newsletterContent.trim()) {
        if (keywords.trim()) {
          toast.success(`Content generated about: ${keywords}`);
        } else {
          toast.success('Content generated from latest recruitment trends!');
        }
      } else {
        toast.success('Content generated successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePost = async () => {
    if (!generatedContent) return;
    
    setApprovingPost(true);
    try {
      const res = await axios.post(`${API}/social/approve-and-generate-image`, {
        headline: generatedContent.headline,
        linkedin_post: generatedContent.linkedin_post,
        x_post: generatedContent.x_post,
        trending_topic: generatedContent.trending_topic,
        key_insights: generatedContent.key_insights,
        llm_provider: llmProvider
      });
      
      // Update with generated image
      setGeneratedContent(prev => ({
        ...prev,
        image_url: res.data.image_url
      }));
      
      setIsApproved(true);
      toast.success('Post approved! Image generated.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate image');
    } finally {
      setApprovingPost(false);
    }
  };

  const handleRegenerate = async () => {
    if (!newsletterContent.trim()) {
      toast.error('No content to regenerate');
      return;
    }
    await handleGenerate();
  };

  const handleRegenerateImage = async (withPrompt = false) => {
    if (!generatedContent?.headline) {
      toast.error('No headline available for image generation');
      return;
    }

    if (withPrompt) {
      setShowImagePrompt(true);
      return;
    }

    setRegeneratingImage(true);
    try {
      const res = await axios.post(`${API}/social/regenerate-image`, {
        headline: generatedContent.headline,
        prompt: imagePrompt || null,
        llm_provider: llmProvider
      });
      
      // Update the image URL in generated content
      setGeneratedContent(prev => ({
        ...prev,
        image_url: res.data.image_url
      }));
      
      toast.success('Image regenerated successfully!');
      setShowImagePrompt(false);
      setImagePrompt('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate image');
    } finally {
      setRegeneratingImage(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'linkedin') {
        setCopiedLinkedIn(true);
        setTimeout(() => setCopiedLinkedIn(false), 2000);
      } else {
        setCopiedX(true);
        setTimeout(() => setCopiedX(false), 2000);
      }
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const isLinkedInConnected = socialAccounts.some(a => a.provider === 'linkedin' && a.status === 'connected');
  const isXConnected = socialAccounts.some(a => a.provider === 'x' && a.status === 'connected');

  const handlePostToLinkedIn = async () => {
    if (!isLinkedInConnected) {
      toast.error('Please connect your LinkedIn account in Profile settings first');
      return;
    }
    if (!generatedContent?.linkedin_post) {
      toast.error('No content to post');
      return;
    }
    setPosting(prev => ({ ...prev, linkedin: true }));
    try {
      await axios.post(`${API}/social/post/linkedin`, {
        content: generatedContent.linkedin_post,
        image_url: postTextOnly ? null : (generatedContent.image_url || null)  // Skip image if text-only checked
      });
      toast.success(postTextOnly ? 'Posted to LinkedIn (text only)!' : 'Posted to LinkedIn with image!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to post to LinkedIn';
      toast.error(message);
    } finally {
      setPosting(prev => ({ ...prev, linkedin: false }));
    }
  };

  const handlePostToX = async () => {
    if (!isXConnected) {
      toast.error('Please connect your X account in Profile settings first');
      return;
    }
    if (!generatedContent?.x_post) {
      toast.error('No content to post');
      return;
    }
    setPosting(prev => ({ ...prev, x: true }));
    try {
      await axios.post(`${API}/social/post/x`, {
        content: generatedContent.x_post,
        image_url: postTextOnly ? null : (generatedContent.image_url || null)  // Skip image if text-only checked
      });
      toast.success(postTextOnly ? 'Posted to X (text only)!' : 'Posted to X with image!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to post to X';
      toast.error(message);
    } finally {
      setPosting(prev => ({ ...prev, x: false }));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Social Content Creator</h1>
          <p className="text-slate-500 mt-1">Generate LinkedIn and X posts from newsletter content using AI</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Reset/New Button */}
          {generatedContent && (
            <Button 
              variant="outline" 
              onClick={handleReset}
              className="gap-2 border-orange-300 text-orange-700 hover:bg-orange-50"
              data-testid="reset-content-btn"
            >
              <RefreshCw className="h-4 w-4" />
              New Content
            </Button>
          )}
          <Badge variant={isLinkedInConnected ? 'default' : 'secondary'} className="gap-1">
            <Linkedin className="h-3 w-3" />
            {isLinkedInConnected ? 'Connected' : 'Not Connected'}
          </Badge>
          <Badge variant={isXConnected ? 'default' : 'secondary'} className="gap-1">
            <Twitter className="h-3 w-3" />
            {isXConnected ? 'Connected' : 'Not Connected'}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Side - Input */}
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-yellow-500" />
              Content Source
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* URL Input */}
            <div className="space-y-2">
              <Label htmlFor="url-input" className="text-sm font-medium flex items-center gap-1">
                <Link2 className="h-3.5 w-3.5" />
                URL (Optional - Highest Priority)
              </Label>
              <Input
                id="url-input"
                type="url"
                placeholder="https://techcrunch.com/article-url or any news/blog/LinkedIn URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="h-10 font-mono text-sm"
              />
              <p className="text-xs text-slate-500">
                Paste URL of any news article, blog post, or webpage to extract and use its content.
              </p>
            </div>

            {/* Keywords Input */}
            <div className="space-y-2">
              <Label htmlFor="keywords-input" className="text-sm font-medium flex items-center gap-1">
                <TrendingUp className="h-3.5 w-3.5" />
                Search Keywords (Optional)
              </Label>
              <Input
                id="keywords-input"
                type="text"
                placeholder="e.g., #AIVoice #Recruitment @AIInterview automation"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                className="h-10 font-mono text-sm"
              />
              <p className="text-xs text-slate-500">
                Enter keywords, hashtags, or topics to search for specific news. Leave empty for general recruitment news.
              </p>
            </div>

            {/* Content Textarea */}
            <Textarea
              placeholder="Paste your newsletter or email content here...

OR leave empty to auto-generate from latest USA recruitment news (TechCrunch, TLDR, HR Dive, etc.)

Use keywords above to search for specific topics.

Example: AI research articles, recruitment trends, HR tech news, industry insights..."
              value={newsletterContent}
              onChange={(e) => setNewsletterContent(e.target.value)}
              className="min-h-[550px] resize-none font-mono text-sm"
              data-testid="newsletter-input"
            />
            
            {/* LLM Provider Selection */}
            <div className="space-y-2">
              <Label htmlFor="llm-provider-select" className="text-sm font-medium flex items-center gap-1">
                <Zap className="h-3.5 w-3.5" />
                AI Provider (Text Generation)
              </Label>
              <Select value={llmProvider} onValueChange={setLlmProvider}>
                <SelectTrigger id="llm-provider-select" className="h-9" data-testid="llm-provider-select">
                  <SelectValue placeholder="Select AI provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="qwen">Qwen 3.5 — DGX (Default)</SelectItem>
                  <SelectItem value="anthropic">Claude AI</SelectItem>
                  <SelectItem value="routerllm">RouterLLM / Abacus AI</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Choose AI service for content generation.
              </p>
            </div>
            
            {/* Tone and Size Dropdowns + Word Count */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="tone-select" className="text-sm font-medium flex items-center gap-1">
                  <Settings className="h-3.5 w-3.5" />
                  Tone
                </Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger id="tone-select" className="h-9">
                    <SelectValue placeholder="Select tone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="polite">Polite</SelectItem>
                    <SelectItem value="sympathetic">Sympathetic</SelectItem>
                    <SelectItem value="aggressive">Aggressive</SelectItem>
                    <SelectItem value="futuristic">Futuristic</SelectItem>
                    <SelectItem value="realistic">Realistic</SelectItem>
                    <SelectItem value="human">Human</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="size-select" className="text-sm font-medium flex items-center gap-1">
                  <TrendingUp className="h-3.5 w-3.5" />
                  Size
                </Label>
                <Select value={size} onValueChange={setSize} disabled={wordCount && wordCount.trim()}>
                  <SelectTrigger id="size-select" className="h-9">
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shorter">Shorter</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="longer">Longer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="word-count-input" className="text-sm font-medium flex items-center gap-1">
                  <TrendingUp className="h-3.5 w-3.5" />
                  Word Count
                </Label>
                <Input
                  id="word-count-input"
                  type="number"
                  min="50"
                  max="500"
                  placeholder="Optional"
                  value={wordCount}
                  onChange={(e) => setWordCount(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
            
            {/* Helper text for word count */}
            {wordCount && wordCount.trim() && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                ⚠️ Word count ({wordCount} words) will override Size dropdown. X post will be shorter than LinkedIn.
              </p>
            )}
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">
                {newsletterContent.length > 0 
                  ? `${newsletterContent.length} characters` 
                  : keywords.trim() 
                    ? `Will search news about: ${keywords}` 
                    : 'No content - will search latest recruitment news'}
              </span>
              <Button
                onClick={handleGenerate}
                disabled={loading}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                data-testid="generate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {newsletterContent.length > 0 ? 'Analyzing...' : 'Searching News...'}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate with AI
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Right Side - Generated Content */}
        <div className="space-y-4">
          {/* Trending Topic */}
          {generatedContent && (
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Trending Topic</p>
                    <p className="text-lg font-medium text-slate-900 mt-1">{generatedContent.trending_topic}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Headline and Approve/Image Section */}
          {generatedContent && (
            <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-amber-600" />
                    <span className="text-lg">{isApproved ? 'Approved Post' : 'Review & Approve'}</span>
                  </div>
                  {isApproved && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRegenerateImage(true)}
                      disabled={regeneratingImage}
                      className="text-xs"
                    >
                      {regeneratingImage ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <>
                          <ImageIcon className="h-3.5 w-3.5 mr-1" />
                          Regenerate Image Only
                        </>
                      )}
                    </Button>
                  )}
                </CardTitle>
                {!isApproved && (
                  <p className="text-xs text-amber-700 mt-1">
                    Review the headline and posts below. Click "Approve Post" to generate the image.
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-white rounded-lg p-4 border-2 border-amber-300">
                  <h3 className="text-2xl font-bold text-slate-900">{generatedContent.headline}</h3>
                </div>
                
                {/* Approve Button or Image */}
                {!isApproved ? (
                  <div className="text-center py-8 bg-white rounded-lg border-2 border-dashed border-amber-300">
                    <ImageIcon className="h-12 w-12 mx-auto text-amber-400 mb-3" />
                    <p className="text-sm text-slate-600 mb-4">Image will be generated after approval</p>
                    <Button
                      onClick={handleApprovePost}
                      disabled={approvingPost}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    >
                      {approvingPost ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating Image...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Approve Post & Generate Image
                        </>
                      )}
                    </Button>
                  </div>
                ) : generatedContent.image_url ? (
                  <div className="space-y-2">
                    <div className="relative rounded-lg overflow-hidden border-2 border-amber-300 bg-white">
                      <img 
                        src={generatedContent.image_url} 
                        alt={generatedContent.headline}
                        className="w-full h-auto object-contain"
                        style={{ maxHeight: '400px' }}
                        onError={(e) => {
                          console.error('Image failed to load:', generatedContent.image_url);
                          e.target.src = 'https://via.placeholder.com/1200x630/4F46E5/ffffff?text=Image+Generation+In+Progress';
                        }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 text-center">
                      👆 This image will be included with your posts
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-slate-100 rounded-lg">
                    <Loader2 className="h-12 w-12 mx-auto text-slate-400 mb-2 animate-spin" />
                    <p className="text-sm text-slate-600">Generating image...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* LinkedIn Post */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Linkedin className="h-5 w-5 text-[#0077B5]" />
                  LinkedIn Post
                </div>
                {generatedContent && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(generatedContent.linkedin_post, 'linkedin')}
                    >
                      {copiedLinkedIn ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                    <Button
                      size="sm"
                      onClick={handlePostToLinkedIn}
                      disabled={posting.linkedin || !isLinkedInConnected}
                      className="bg-[#0077B5] hover:bg-[#006097]"
                    >
                      {posting.linkedin ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-1" />
                          Post
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {generatedContent ? (
                <>
                  <Textarea
                    value={generatedContent.linkedin_post}
                    onChange={(e) => setGeneratedContent(prev => ({...prev, linkedin_post: e.target.value}))}
                    className="min-h-[200px] text-sm font-mono resize-none"
                  />
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={postTextOnly}
                        onChange={(e) => setPostTextOnly(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300"
                        data-testid="linkedin-text-only-checkbox"
                      />
                      <span className="font-medium">Post Text Only (No Image)</span>
                    </label>
                  </div>
                </>
              ) : (
                <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-400 italic min-h-[200px] flex items-center justify-center">
                  LinkedIn post will appear here after AI generation
                </div>
              )}
            </CardContent>
          </Card>

          {/* X Post */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Twitter className="h-5 w-5 text-slate-900" />
                  X (Twitter) Post
                </div>
                {generatedContent && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(generatedContent.x_post, 'x')}
                    >
                      {copiedX ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                    <Button
                      size="sm"
                      onClick={handlePostToX}
                      disabled={posting.x || !isXConnected}
                      className="bg-slate-900 hover:bg-slate-800"
                    >
                      {posting.x ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-1" />
                          Post
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {generatedContent ? (
                <>
                  <Textarea
                    value={generatedContent.x_post}
                    onChange={(e) => setGeneratedContent(prev => ({...prev, x_post: e.target.value}))}
                    className="min-h-[120px] text-sm font-mono resize-none"
                    maxLength={280}
                  />
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={postTextOnly}
                        onChange={(e) => setPostTextOnly(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300"
                        data-testid="x-text-only-checkbox"
                      />
                      <span className="font-medium">Post Text Only (No Image)</span>
                    </label>
                    <span className={generatedContent.x_post.length > 280 ? 'text-red-500 font-medium' : ''}>
                      {generatedContent.x_post.length}/280 characters
                    </span>
                  </div>
                </>
              ) : (
                <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-400 italic flex items-center justify-center">
                  X post will appear here after AI generation
                </div>
              )}
            </CardContent>
          </Card>

          {/* Key Insights */}
          {generatedContent && generatedContent.key_insights?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                  Key Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {generatedContent.key_insights.map((insight, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="text-amber-500 font-bold">•</span>
                      {insight}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Regenerate Button */}
          {generatedContent && (
            <div className="space-y-3">
              {/* Settings Display */}
              <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Settings className="h-3 w-3" />
                  Tone: <span className="font-medium text-slate-700 capitalize">{tone}</span>
                </span>
                <span className="text-slate-300">|</span>
                <span className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  Size: <span className="font-medium text-slate-700 capitalize">{size}</span>
                </span>
              </div>
              
              <Button
                variant="outline"
                onClick={handleRegenerate}
                disabled={loading}
                className="w-full"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Regenerate with Current Settings
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Connection Info */}
      {(!isLinkedInConnected || !isXConnected) && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Lightbulb className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-amber-800">Connect Your Social Accounts</p>
                <p className="text-sm text-amber-700 mt-1">
                  To post directly to LinkedIn and X, connect your accounts in the <a href="/profile" className="underline font-medium">Profile Settings</a> page.
                  For LinkedIn, we use Unipile API. For X, you&apos;ll need to provide your API credentials.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Image Prompt Dialog */}
      <Dialog open={showImagePrompt} onOpenChange={setShowImagePrompt}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-indigo-600" />
              Regenerate Image
            </DialogTitle>
            <DialogDescription>
              Optionally enter a custom prompt to guide the image generation. Leave blank to use the headline only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="image-prompt">Custom Prompt (Optional)</Label>
              <Textarea
                id="image-prompt"
                placeholder="E.g., 'Use vibrant colors with tech theme' or 'Show diverse candidates in modern office'"
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                className="min-h-[100px]"
              />
              <p className="text-xs text-slate-500">
                The AI will use your headline along with this custom prompt to generate a new image.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowImagePrompt(false);
                setImagePrompt('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleRegenerateImage(false)}
              disabled={regeneratingImage}
              className="bg-gradient-to-r from-indigo-600 to-purple-600"
            >
              {regeneratingImage ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Image
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
